import type { ConsultationResponseDto, WaitlistAvailability } from '@montugroup/pms-api-contracts';
import { useCallback, useState } from 'react';

import type { WaitlistJoinSource } from '@/hooks/analytics/useConsultationWaitlistTracking';
import { useConsultationWaitlistTracking } from '@/hooks/analytics/useConsultationWaitlistTracking';
import type { WaitlistJoinOutcome } from '@/hooks/consultations/useJoinConsultationWaitlist';
import { useJoinConsultationWaitlist } from '@/hooks/consultations/useJoinConsultationWaitlist';
import { WAITLIST_GENERIC_ERROR_MESSAGE } from '@/pages/consultations/components/waitlist.config';

export type WaitlistFormMode = 'join' | 'change';

/**
 * Submits the waitlist preference form: joins or changes the preference, shows the generic error when it fails and
 * tracks a successful join or change. Callers handle what to show for each outcome.
 * @param source - where the join was made from, for tracking
 * @returns a submit that resolves with the outcome, whether it's pending, the error to show and a way to clear it
 */
export function useSubmitWaitlistPreference(source: WaitlistJoinSource = 'card') {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { join, isPending } = useJoinConsultationWaitlist();
  const { trackWaitlistJoined, trackWaitlistPreferenceChanged } = useConsultationWaitlistTracking();

  const submit = useCallback(
    async (
      consultation: Pick<ConsultationResponseDto, 'id' | 'code'>,
      availability: WaitlistAvailability,
      mode: WaitlistFormMode,
    ): Promise<WaitlistJoinOutcome> => {
      setErrorMessage(null);
      const outcome = await join(consultation.code, availability);
      if (outcome === 'failed') {
        setErrorMessage(WAITLIST_GENERIC_ERROR_MESSAGE);
      } else if (outcome === 'joined' && mode === 'change') {
        trackWaitlistPreferenceChanged(consultation.id, availability);
      } else if (outcome === 'joined') {
        trackWaitlistJoined(consultation.id, availability, source);
      }
      return outcome;
    },
    [join, source, trackWaitlistJoined, trackWaitlistPreferenceChanged],
  );

  const clearError = useCallback(() => setErrorMessage(null), []);

  return { submit, isPending, errorMessage, clearError };
}
